import { Dropdown } from '@carbon/react';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { formulaDetailsAtomFamily } from 'atoms/formula-details-atoms';
import type { AcreageBasis, UatAcreageBasis } from '../types';
import {
  describeSaveFailure,
  saveAcreageBasisAtom,
  type SaveResponse,
  recalculationDeferred,
  recalculationFailed,
} from './acreage-basis-atoms';
import styles from './acreage-basis-dropdown.module.scss';

interface BasisOption {
  id: AcreageBasis;
  label: string;
}

const OPTIONS: BasisOption[] = [
  { id: 'gis', label: 'GIS acreage' },
  { id: 'legal', label: 'Legal acreage' },
];

const NO_ALLOCATION_MANAGER =
  'Legal acreage needs the unit to have an Allocation Manager, which is where the plat figures are recorded.';

const NO_PLAT_ACREAGE =
  'Legal acreage needs a plat acreage for this tract, and the Allocation Manager for this unit records none.';

/**
 * Why legal acreage cannot be selected, or undefined when it can.
 *
 * The two causes read the same from the outside and are not the same problem:
 * a unit with no Allocation Manager has nothing recorded anywhere, while a unit
 * whose line item carries a zero acreage has a record that legal basis cannot
 * divide by. Offering legal basis on the second saved it, displayed it, and
 * computed GIS.
 *
 * @param {UatAcreageBasis | undefined} basis - The UAT's stored basis data
 * @return {string | undefined} The explanation to show beneath the dropdown
 */
const unavailableReason = (
  basis: UatAcreageBasis | undefined
): string | undefined => {
  if (basis?.platAcreagePresent) return undefined;

  return basis?.allocationManagerPresent
    ? NO_PLAT_ACREAGE
    : NO_ALLOCATION_MANAGER;
};

interface AcreageBasisDropdownProps {
  subtractId: string;
  uatGisUuid: string | undefined;
  basis: UatAcreageBasis | undefined;
  /**
   * Identifies the cached formula payload for this UAT, so a basis change can
   * drop it. The popup is built from that cache, and without this it keeps
   * showing the pre-change basis and its numbers until a full page load.
   */
  nriCalculationId?: number | null;
}

/**
 * Chooses which acreage figures drive this UAT's participation in the NRI
 * calculation. Sits on the card rather than inside the calculation popup so the
 * selection is visible without opening a modal per UAT.
 *
 * Legal acreage is unavailable where the unit records no plat acreage for the
 * tract — that is what it divides by, so there is nothing to calculate against.
 * The server enforces the same rule.
 *
 * @param {AcreageBasisDropdownProps} props - The component props.
 * @return {JSX.Element | null} The dropdown, or null when the UAT is unidentified.
 */
export const AcreageBasisDropdown = ({
  subtractId,
  uatGisUuid,
  basis,
  nriCalculationId,
}: AcreageBasisDropdownProps): JSX.Element | null => {
  const save = useSetAtom(saveAcreageBasisAtom);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // A save that landed but whose figures are not current is not an error, and
  // the invalid state would paint the control red for a successful change.
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  if (!uatGisUuid) return null;

  const blockedReason = unavailableReason(basis);
  const selectedId = basis?.acreageBasis ?? 'gis';

  // Disabled only when there is nothing the user could usefully pick: legal is
  // unavailable and they are already on GIS.
  //
  // A UAT already set to legal that later loses its plat acreage — the
  // Allocation Manager changes, or a lateral-allocated unit records none — must
  // stay changeable, or the control is dead on exactly the row that needs
  // correcting and there is no way back to GIS. That is the shape of the
  // "cannot get back to GIS" report this feature set out to fix.
  const nothingToChange = blockedReason !== undefined && selectedId === 'gis';
  const selectedItem =
    OPTIONS.find((option) => option.id === selectedId) ?? OPTIONS[0];

  return (
    <div
      className={styles.container}
      // The card is itself clickable to select the UAT; without this, changing
      // the basis would also reselect and scroll the list.
      onClick={(event) => event.stopPropagation()}
    >
      <Dropdown
        id={`acreage-basis-${uatGisUuid}`}
        size="sm"
        titleText="Acreage basis"
        label="Acreage basis"
        hideLabel={false}
        disabled={nothingToChange || saving}
        // Carbon renders helperText only on the stacked variant; the inline one
        // both swallowed it and overflowed the card's left column into the
        // divider, because the label sits beside the control rather than above.
        helperText={blockedReason}
        invalid={saveError !== null}
        invalidText={saveError ?? undefined}
        warn={saveNotice !== null}
        warnText={saveNotice ?? undefined}
        items={OPTIONS}
        itemToString={(item: BasisOption | null) => item?.label ?? ''}
        selectedItem={selectedItem}
        onChange={async ({
          selectedItem: next,
        }: {
          selectedItem: BasisOption | null;
        }) => {
          if (!next || next.id === selectedId) return;

          setSaving(true);
          setSaveError(null);
          setSaveNotice(null);

          // Awaited, and the outcome reported. The control is driven entirely by
          // the stored basis, so a save that fails leaves it showing the old
          // value with no explanation — which reads as "the dropdown will not
          // let me change it" rather than "the server refused".
          const response: SaveResponse | undefined = await save({
            subtractId,
            uatGisUuid,
            acreageBasis: next.id,
            // Switching basis leaves any existing override in place; it is only
            // consulted under legal basis.
            overlapFraction: basis?.overlapFraction ?? null,
          });

          setSaving(false);

          if (response?.type !== 'success') {
            setSaveError(
              describeSaveFailure(
                response,
                'The acreage basis could not be changed.'
              )
            );
            return;
          }

          // Saved, but the figures derived from it were not rewritten. Reported
          // here rather than swallowed: the calculator logs its failures and
          // returns normally, so without this the card shows the new basis over
          // the old numbers with nothing to say so.
          if (recalculationFailed(response)) {
            setSaveNotice(
              'Saved, but the interest figures could not be recalculated — they still show the previous basis.'
            );
            return;
          }

          // Saved, and the rewrite is running in the background because this
          // Asset has too many calculations to redo inside the request. Nothing
          // announces when it finishes, so say so rather than let the unchanged
          // figures read as current.
          if (recalculationDeferred(response)) {
            setSaveNotice(
              'Saved. The interest figures are being recalculated in the background — reload in a moment to see them.'
            );
          }

          // The popup renders from the cached formula payload, which still
          // describes the previous basis until the recalculation restamps it.
          if (nriCalculationId)
            formulaDetailsAtomFamily.remove(nriCalculationId);
        }}
      />
      {/* Carbon renders invalidText as .cds--form-requirement with NO live
          region, so a failed save is silent to a screen reader — this mirror
          announces it (#7418 review). Visually hidden: the red text above
          already shows it to sighted users. */}
      {saveError !== null && (
        <div role="alert" className={styles.visuallyHidden}>
          {saveError}
        </div>
      )}
    </div>
  );
};
